// Utilities & Hooks
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../providers/auth-context";
import fetchHandler from "../fetchHandler";

// Interfaces
import { CompaniesResponseFields } from "./interfaces";

/**
 *
 * Get list of all companies to which the logged in user has access to
 *
 */
export function useCompaniesGetAvailable() {
  return useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      return (await fetchHandler("GET", "company")) as CompaniesResponseFields[];
    },
    meta: {
      errorMessage: "Failed getting list of available companies",
    },
  });
}

/**
 *
 * Get the list of positions that are available
 * within the company that we're currently viewing
 *
 */
export function useCompaniesGetAvailablePositions() {
  const { user } = useAuth();
  const companySlug: string = user.active_company.slug;

  return useQuery({
    queryKey: ["companies-positions", companySlug],
    queryFn: async () => {
      return (await fetchHandler("GET", `company/${companySlug}/positions`)) as string[];
    },
    enabled: !!companySlug,
  });
}

/**
 *
 * Get the list of all positions for specific company
 *
 */
export function useCompaniesGetSpecificPositions(companySlug?: string) {
  return useQuery({
    queryKey: ["companies-positions", companySlug],
    queryFn: async () => {
      return (await fetchHandler("GET", `company/${companySlug}/positions`)) as string[];
    },
    enabled: !!companySlug,
  });
}

/**
 *
 * Get only the `currently` existing job positions for the selected company
 *
 */
export function useCompaniesGetJobPositionsForCurrentCompany() {
  const { user } = useAuth();
  const companySlug: string = user.active_company.slug;

  return useQuery({
    queryKey: ["job-positions-current-company", companySlug],
    queryFn: async () => {
      return (await fetchHandler("GET", `company/${companySlug}/job-positions`)) as string[];
    },
    enabled: !!companySlug,
  });
}
